import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import PanelLayout from '../layouts/PanelLayout';
import { authorService } from '../../services/authorService';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import SearchableSelect from '../../components/common/SearchableSelect';
import Loader from '../../components/Loader';

const STATUS_OPTIONS = [
   { id: 'ACTIVE', name: 'Active' },
   { id: 'INACTIVE', name: 'Inactive' },
];

const AuthorForm = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const isEdit = !!id;

   const [loading, setLoading] = useState(isEdit);
   const [saving, setSaving] = useState(false);
   const [imagePreview, setImagePreview] = useState('');
   const [formData, setFormData] = useState({
      name: '',
      slug: '',
      bio: '',
      email: '',
      image: '',
      status: 'ACTIVE',
   });

   useEffect(() => {
      if (isEdit) fetchAuthor();
   }, [id]);

   const fetchAuthor = async () => {
      try {
         setLoading(true);
         const response = await authorService.getById(id);
         const author = response.data;

         let imagePath = '';
         if (author.image) {
            try {
               const url = new URL(author.image);
               imagePath = url.pathname.replace('/uploads/', '');
            } catch {
               imagePath = author.image.replace(/^.*\/uploads\//, '');
            }
         }

         setFormData({
            name: author.name || '',
            slug: author.slug || '',
            bio: author.bio || '',
            email: author.email || '',
            image: imagePath,
            status: author.status || 'ACTIVE',
         });
         setImagePreview(author.image || '');
      } catch (error) {
         console.error('Error fetching author:', error);
         toast.error('Failed to load author');
         navigate('/admin/authors');
      } finally {
         setLoading(false);
      }
   };

   const handleImageUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
         const response = await authorService.uploadImage(file);
         setFormData((prev) => ({ ...prev, image: response.data.path }));
         setImagePreview(response.data.url);
      } catch (error) {
         console.error('Error uploading image:', error);
         toast.error('Failed to upload image');
      }
   };

   const formatSlug = (text) =>
      text
         .toLowerCase()
         .replace(/\s+/g, '-')
         .replace(/[^a-z0-9-]/g, '')
         .replace(/-+/g, '-')
         .replace(/^-|-$/g, '');

   const handleNameChange = (e) => {
      const name = e.target.value;
      setFormData((prev) => ({
         ...prev,
         name,
         slug: !isEdit ? formatSlug(name) : prev.slug,
      }));
   };

   const handleSubmit = async () => {
      if (!formData.name.trim() || !formData.slug.trim()) {
         toast.error('Name and slug are required');
         return;
      }

      const payload = { ...formData };

      try {
         setSaving(true);
         if (isEdit) {
            await authorService.update(id, payload);
            toast.success('Author updated successfully');
         } else {
            await authorService.create(payload);
            toast.success('Author created successfully');
         }
         navigate('/admin/authors');
      } catch (error) {
         console.error('Error saving author:', error);
         const errorMessage =
            error.response?.data?.errors?.map((e) => e.msg).join(', ') ||
            error.response?.data?.message ||
            'Failed to save author';
         toast.error(errorMessage);
      } finally {
         setSaving(false);
      }
   };

   if (loading) {
      return (
         <PanelLayout>
            <Loader />
         </PanelLayout>
      );
   }

   return (
      <PanelLayout>
         <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-4">
               <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {isEdit ? 'Edit Author' : 'Create New Author'}
               </h1>
               <div className="flex gap-3">
                  <Button
                     variant="outline"
                     onClick={() => navigate('/admin/authors')}
                     disabled={saving}
                  >
                     Cancel
                  </Button>
                  <Button onClick={handleSubmit} disabled={saving}>
                     {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                  </Button>
               </div>
            </div>

            <div className="space-y-6">
               {/* Profile image + Name + Slug row */}
               <div className="flex flex-col sm:flex-row gap-6">
                  {/* Avatar */}
                  <div className="flex flex-col items-center gap-3 sm:w-48 flex-shrink-0">
                     {imagePreview ? (
                        <img
                           src={imagePreview}
                           alt="Profile preview"
                           className="w-24 h-24 rounded-full object-cover border border-gray-200 dark:border-gray-700"
                        />
                     ) : (
                        <div className="w-24 h-24 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                           <span className="text-3xl font-bold text-gray-400 dark:text-gray-500">
                              {formData.name ? formData.name.charAt(0).toUpperCase() : '?'}
                           </span>
                        </div>
                     )}
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Profile Image
                     </label>
                     <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="w-full text-xs text-gray-500 dark:text-gray-400 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-dark-accent dark:file:text-white"
                     />
                  </div>

                  {/* Name + Slug */}
                  <div className="flex-1 space-y-4">
                     <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                           value={formData.name}
                           onChange={handleNameChange}
                           placeholder="Author full name"
                        />
                     </div>
                     <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           Slug <span className="text-red-500">*</span>
                        </label>
                        <Input
                           value={formData.slug}
                           onChange={(e) =>
                              setFormData((prev) => ({ ...prev, slug: formatSlug(e.target.value) }))
                           }
                           placeholder="author-slug"
                        />
                        {formData.slug && (
                           <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              /author/{formData.slug}
                           </p>
                        )}
                     </div>
                  </div>
               </div>

               {/* Bio */}
               <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     Short Bio
                  </label>
                  <textarea
                     value={formData.bio}
                     onChange={(e) => setFormData((prev) => ({ ...prev, bio: e.target.value }))}
                     placeholder="Brief description about the author..."
                     rows="3"
                     className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-secondary px-4 py-2 text-gray-900 dark:text-gray-100 text-sm"
                  />
               </div>

               {/* Email + Status */}
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email (optional)
                     </label>
                     <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                           setFormData((prev) => ({ ...prev, email: e.target.value }))
                        }
                        placeholder="author@example.com"
                     />
                  </div>
                  <div>
                     <SearchableSelect
                        label="Status"
                        searchable={false}
                        options={STATUS_OPTIONS}
                        value={formData.status}
                        onChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}
                        className="!mb-0"
                     />
                  </div>
               </div>
            </div>
         </div>
      </PanelLayout>
   );
};

export default AuthorForm;
